import { useMemo } from "react";

/**
 * Copy of the booking / lead cards. The package stays i18n-free: hosts pass a (partial) set built
 * from their own catalogs; these English defaults keep the components usable on their own.
 */
export interface ActionLabels {
  retry: string;
  sending: string;

  // Details form
  detailsHeading: string;
  required: string;
  invalidEmail: string;
  invalidMobile: string;
  contactRule: string;
  phoneHint: string;
  choose: string;
  consent: string;
  privacyLink: string;
  consentRequired: string;
  checkField: string;
  leadSubmit: string;

  // Booking
  requestIntro: string;
  calendarIntro: string;
  requestThisTime: string;
  bookThisTime: string;
  minutes: (minutes: number) => string;
  yourTime: string;
  pickATime: string;

  // Slot picker
  chooseDay: string;
  chooseTime: string;
  loadingTimes: string;
  timesIn: (zone: string) => string;
  dayChip: (date: string, count: number) => string;
  showMoreDays: string;
  loadingMoreDays: string;
  noTimesTitle: string;
  noTimesBody: string;
  nextOpen: (when: string) => string;
  jumpTo: (date: string) => string;
  slotsError: string;

  // Result card
  addToCalendar: string;
  joinMeet: string;
  stateSent: string;
  stateRequested: string;
  stateConfirmed: string;
  stateFailed: string;
  completed: string;

  // Disabled cards
  expired: string;
  unavailable: string;

  // Failures
  slotTaken: string;
  validation: string;
  rateLimited: (seconds: number | undefined) => string;
  serviceUnavailable: string;
  conflict: string;
  captcha: string;
  signIn: string;
  generic: string;

  // Playground preview
  previewNote: string;
  previewTimes: string;
}

export const DEFAULT_ACTION_LABELS: ActionLabels = {
  retry: "Try again",
  sending: "Sending…",

  detailsHeading: "Your details",
  required: "This field is required",
  invalidEmail: "Enter a valid email address",
  invalidMobile: "Enter the number with its country code, e.g. +1 415 555 0123",
  contactRule: "Add an email or a phone number so we can reach you",
  phoneHint: "Optional · include your country code",
  choose: "Choose…",
  consent: "I agree to the privacy notice",
  privacyLink: "Read the privacy notice",
  consentRequired: "Please agree to the privacy notice to continue",
  checkField: "Check this field",
  leadSubmit: "Send my details",

  requestIntro: "Pick a time and send your details. The team confirms it by email — it isn't booked until they do.",
  calendarIntro: "Pick a time and it's booked right away.",
  requestThisTime: "Request this time",
  bookThisTime: "Book this time",
  minutes: (minutes) => `${String(minutes)} min`,
  yourTime: "Your time",
  pickATime: "Pick a time first",

  chooseDay: "Choose a day",
  chooseTime: "Choose a time",
  loadingTimes: "Loading available times",
  timesIn: (zone) => `Times are shown in your time zone (${zone})`,
  dayChip: (date, count) => `${date}, ${String(count)} ${count === 1 ? "time" : "times"} open`,
  showMoreDays: "Show more days",
  loadingMoreDays: "Loading more days",
  noTimesTitle: "No open times in these days",
  noTimesBody: "Try later dates.",
  nextOpen: (when) => `The next open time is ${when}.`,
  jumpTo: (date) => `Jump to ${date}`,
  slotsError: "We couldn't load the available times.",

  addToCalendar: "Add to calendar",
  joinMeet: "Join the video call",
  stateSent: "Sent",
  stateRequested: "Not confirmed yet",
  stateConfirmed: "Confirmed",
  stateFailed: "Not completed",
  completed: "Done",

  expired: "This request expired — ask again",
  unavailable: "This request is no longer available — ask again",

  slotTaken: "That time was just taken. Please pick another.",
  validation: "Check the highlighted details and try again.",
  rateLimited: (seconds) => (seconds === undefined ? "Too many tries. Please wait a moment and try again." : `Too many tries. Try again in ${String(seconds)} s.`),
  serviceUnavailable: "This is temporarily unavailable. Please try again in a moment.",
  conflict: "This was already sent or is still being processed. Check your email, or ask again.",
  captcha: "Please wait for the security check to finish, then try again.",
  signIn: "Please sign in to continue.",
  generic: "Something went wrong. Please try again.",

  previewNote: "Preview — actions run for real visitors",
  previewTimes: "Open times appear here for real visitors.",
};

export function resolveActionLabels(overrides?: Partial<ActionLabels>): ActionLabels {
  return overrides ? { ...DEFAULT_ACTION_LABELS, ...overrides } : DEFAULT_ACTION_LABELS;
}

/** Stable merged labels (hosts should memoize `overrides`; the result changes only when it does). */
export function useActionLabels(overrides?: Partial<ActionLabels>): ActionLabels {
  return useMemo(() => resolveActionLabels(overrides), [overrides]);
}
